import React from "react";

import { Controller, useFormContext } from "react-hook-form";

import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { PillToggle } from "src/base-components/PillToggle";
import { type SchemaEnumErrorStatus } from "src/schema/SchemaEnumRow";
import { SchemaEnumRows } from "src/schema/SchemaEnumRows";
import {
  EnumValueObject,
  isEnumVariableReference,
} from "src/schema/schemaMappingUtils";

type EnumValueSource = "fixed" | "variable";

// The input shows the field path (`allowed_values`); the form stores the full
// runtime expression (`$data.allowed_values`). These two are exact inverses.
const DATA_NAMESPACE = "$data";

export const toStoredExpression = (fieldPath: string): string => {
  if (fieldPath.trim() === "") {
    return "";
  }
  // A bracket key (`["1options"]`, inserted by autocomplete for a
  // non-identifier field) or an explicit leading dot is already an accessor;
  // adding another dot would corrupt it into `$data.["1options"]`.
  const opensWithAccessor = fieldPath[0] === "[" || fieldPath[0] === ".";
  return `${DATA_NAMESPACE}${opensWithAccessor ? "" : "."}${fieldPath}`;
};

export const toDisplayedFieldPath = (storedValue: unknown): string => {
  if (
    typeof storedValue !== "string" ||
    !storedValue.startsWith(DATA_NAMESPACE)
  ) {
    return "";
  }
  const accessor = storedValue.slice(DATA_NAMESPACE.length);
  // Guard against "$data" lookalikes like "$database": the namespace must be
  // followed by an accessor (a dot or bracket), or be exactly "$data".
  if (accessor !== "" && accessor[0] !== "." && accessor[0] !== "[") {
    return "";
  }
  return accessor.startsWith(".") ? accessor.slice(1) : accessor;
};

type Props = {
  enumFormLocation: string;
  enumValue: EnumValueObject[];
  immutable: boolean;
  errorStatus?: SchemaEnumErrorStatus;
  onSourceChange?: (next: EnumValueSource) => void;
  onChange?: () => void;
};

export const JsonSchemaEnumEditor: React.FC<Props> = ({
  enumFormLocation,
  enumValue,
  immutable,
  errorStatus,
  onSourceChange,
  onChange,
}) => {
  const { control, setValue, clearErrors } = useFormContext();
  const source: EnumValueSource = isEnumVariableReference(enumValue)
    ? "variable"
    : "fixed";

  const handleSourceChange = (next: EnumValueSource) => {
    if (next === source) {
      return;
    }
    // Fixed mode validates every row, so clear the whole enum's error subtree
    // (not just row 0) before replacing the array - otherwise errors on higher
    // indices linger in form state and resurface on rows re-added later.
    clearErrors(enumFormLocation);
    setValue(
      enumFormLocation,
      next === "variable"
        ? [{ value: "", isVariableReference: true }]
        : [{ value: '"value_1"' }],
    );
    onSourceChange?.(next);
    onChange?.();
  };

  return (
    <div className="mt-2 ml-7 flex flex-col gap-y-3">
      <PillToggle value={source} onChange={handleSourceChange}>
        <PillToggle.Button disabled={immutable} value="fixed">
          Fixed values
        </PillToggle.Button>
        <PillToggle.Button disabled={immutable} value="variable">
          From variable
        </PillToggle.Button>
      </PillToggle>
      {source === "fixed" ? (
        <SchemaEnumRows
          enumFormLocation={enumFormLocation}
          errorStatus={errorStatus}
          immutable={immutable}
          withValidation
        />
      ) : (
        <div className="my-4 ml-2 flex flex-col gap-y-1 border-gray-200 border-l-2 pl-3">
          <Controller
            control={control}
            name={`${enumFormLocation}.0.value`}
            render={({ field }) => (
              <AutocompleteCodeInput
                dataLoc="ai-node-enum-variable-input"
                disabled={immutable}
                placeholder="field"
                prefix="data."
                value={toDisplayedFieldPath(field.value)}
                onChange={(value) => {
                  field.onChange(toStoredExpression(value));
                  onChange?.();
                }}
              />
            )}
          />
          <span className="font-inter-normal-12px text-gray-500">
            Each item in the list becomes an allowed value.
          </span>
        </div>
      )}
    </div>
  );
};
